import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@volca/ui/components/ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
} from "@volca/ui/components/ui/item";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import {
  BUSINESS_SETTINGS_ROUTES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { REFERRAL_PALETTE } from "@/core/colors";
import { getCustomerNameOrPhone } from "@/customer/utils";
import {
  REFERRAL_FIELD,
  REFERRAL_FIELD_TO_SORTER,
  REFFERRAL_STATUS_TO_SIDEBAR_TEXT,
} from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";

interface ReferralListItemProps {
  referral: IReferral;
}

const ReferralListItem = ({ referral }: ReferralListItemProps) => {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  return (
    <Item
      render={<button type="button" />}
      className="h-12 rounded-lg px-2 py-0 text-left"
      onClick={() => {
        void navigate(
          `/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.referralHistory}`,
        );
        updateSidebar({
          section: SIDEBAR_SECTIONS.referralHistory,
          mode: SIDEBAR_MODES.view,
          selectedItemId: referral.id,
          isOpen: true,
        });
      }}
    >
      <ItemContent>
        <ItemTitle>
          {getCustomerNameOrPhone(referral.potential_customer)}
        </ItemTitle>
      </ItemContent>
      <ItemActions className="gap-4">
        <Badge
          variant="secondary"
          color={REFERRAL_PALETTE[referral.status].tone}
        >
          {REFFERRAL_STATUS_TO_SIDEBAR_TEXT[referral.status]}
        </Badge>
        <CaretRightIcon aria-hidden className="size-4 text-subtle" />
      </ItemActions>
    </Item>
  );
};

interface ReferralListProps {
  referrals: IReferral[];
  isLoading: boolean;
  title?: string;
  hideHeader?: boolean;
}

export const ReferralList = ({
  referrals,
  isLoading,
  title = "Assignments",
  hideHeader,
}: ReferralListProps) => {
  const sortedReferrals = useMemo(
    () => [...referrals].sort(REFERRAL_FIELD_TO_SORTER[REFERRAL_FIELD.status]),
    [referrals],
  );

  return (
    <Stack align="start" gap={4} className="h-full w-full px-6 pr-1">
      {!hideHeader && (
        <Stack
          gap={4}
          direction="row"
          justify="between"
          align="center"
          className="w-full flex-none pr-5"
        >
          <Text variant="title">
            {title} {isLoading ? "" : `(${referrals?.length})`}
          </Text>
        </Stack>
      )}

      {isLoading && (
        <Spinner
          layout="centered"
          role="status"
          aria-label="Loading assigned referrals"
        />
      )}
      {!isLoading && !referrals.length && (
        <Empty className={emptyStateVariants({ layout: "inline" })}>
          <EmptyHeader>
            <EmptyDescription>No referrals assigned</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      <div
        hidden={referrals.length === 0}
        className={cn("min-h-0 w-full flex-1 overflow-y-auto pr-5", {
          "mt-4": hideHeader,
        })}
      >
        <Stack align="start" className="w-full pb-6" gap={1}>
          {sortedReferrals.map((referral) => (
            <ReferralListItem key={referral.id} referral={referral} />
          ))}
        </Stack>
      </div>
    </Stack>
  );
};
